import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import {
  catchError,
  map,
  shareReplay,
  startWith,
  tap,
} from 'rxjs/operators';
import { CiaoModalComponent } from '~app/components/shared/ciao-modal/ciao-modal.component';
import { AppliedPermission } from '~app/models';
import { UserGroupAttributes } from '~app/models/user-group';
import { RolePermissionService } from '~app/services/role-permission.service';
import { UserGroupFormComponent } from '../user-group-form/user-group-form.component';
import { CiaoSharedModule } from '~app/components/shared/shared.module';
import { AsyncPipe, NgIf } from '@angular/common';

@Component({
    selector: 'ciao-user-group-modal',
    imports: [AsyncPipe, NgIf, CiaoSharedModule, UserGroupFormComponent],
    templateUrl: './user-group-modal.component.html',
    styleUrl: './user-group-modal.component.less'
})
export class UserGroupModalComponent {
  constructor(
    private readonly rolePermissionService: RolePermissionService,
    private readonly toastrService: ToastrService,
    private readonly cdr: ChangeDetectorRef
  ) {}
  @ViewChild('viewAndEditModal') viewAndEditModal: CiaoModalComponent;
  @ViewChild('actionUserGroupForm') actionUserGroupForm: UserGroupFormComponent;
  @ViewChild('deleteUserGroupModal') deleteUserGroupModal: CiaoModalComponent;
  @ViewChild('viewTeamModal') viewTeamModal: CiaoModalComponent;
  isEditForm: boolean;
  modalTitle$: Observable<string>;
  canCreateTeam$ = this.getCanCreateTeam$();
  selectedTeamPermissions$ = this.getTeamEditPermissions();
  userGroup: UserGroupAttributes;
  // `null` = Add mode (form shows defaults); `undefined` = unset/closed. The form setter treats these distinctly.
  activeUserGroupId: string | null | undefined = undefined;

  openModal(userGroup: UserGroupAttributes, action: 'Add' | 'Edit') {
    this.userGroup = userGroup;
    if (action === 'Add') {
      this.isEditForm = false;
      this.modalTitle$ = of('Add Team');
      this.canCreateTeam$ = this.getCanCreateTeam$();
    } else {
      this.isEditForm = true;
      this.selectedTeamPermissions$ = this.getTeamEditPermissions(userGroup);
      this.modalTitle$ = this.selectedTeamPermissions$.pipe(
        map(
          (perms) =>
            perms.edit ||
            perms.assignSupervisor ||
            perms.assignTeamMember ||
            perms.assignDispatcher ||
            perms.assignGuest
        ),
        map((canEdit) => (canEdit ? 'Edit Team' : 'View Team')),
        startWith('Loading')
      );
    }
    this.activeUserGroupId = userGroup?.id ?? null;
    this.cdr.detectChanges();
    let modalRef = this.viewAndEditModal.openModal();
    modalRef.result.then(
      (message) => {
        this.userGroup = null;
        this.activeUserGroupId = undefined;
      },
      (message) => {
        this.userGroup = null;
        this.activeUserGroupId = undefined;
      }
    );
  }

  openDeleteUserGroupModal(userGroup) {
    this.deleteUserGroupModal.openModal();
    this.userGroup = userGroup;
  }

  getTeamEditPermissions(userGroup?: UserGroupAttributes) {
    if (!userGroup) {
      return of({
        edit: false,
        remove: false,
        assignTeamMember: false,
        assignSupervisor: false,
        assignDispatcher: false,
        assignGuest: false,
        viewTeamMembers: false,
        clickUpdate: false,
      });
    }
    return this.rolePermissionService
      .searchMyAppliedPermissions({
        permissionIds: [
          'edit_usergroup',
          'delete_usergroup',
          'assign_team_member',
          'assign_supervisor',
          'assign_dispatcher',
          'assign_guest',
          'view_team_members',
        ],
        filterWithinUserGroups: [userGroup],
      })
      .pipe(
        startWith([] as AppliedPermission[]),
        map((perms) => perms.map((perm) => perm.AppliedPermissionId)),
        map((permIds) => ({
          edit: permIds.indexOf('edit_usergroup') !== -1,
          remove: permIds.indexOf('delete_usergroup') !== -1,
          assignTeamMember: permIds.indexOf('assign_team_member') !== -1,
          assignSupervisor: permIds.indexOf('assign_supervisor') !== -1,
          assignDispatcher: permIds.indexOf('assign_dispatcher') !== -1,
          assignGuest: permIds.indexOf('assign_guest') !== -1,
          viewTeamMembers: permIds.indexOf('view_team_members') !== -1,
        })),
        map(
          ({
            edit,
            remove,
            assignTeamMember,
            assignSupervisor,
            assignDispatcher,
            assignGuest,
            viewTeamMembers,
          }) => ({
            edit,
            remove,
            assignTeamMember,
            assignSupervisor,
            assignDispatcher,
            assignGuest,
            viewTeamMembers,
            // Update button is enabled if the user can change ANY of the
            // team's sections — basic info, supervisor list, team-member
            // list, dispatcher list, or guest list.
            clickUpdate:
              edit ||
              assignSupervisor ||
              assignTeamMember ||
              assignDispatcher ||
              assignGuest,
          })
        ),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  getCanCreateTeam$() {
    return this.rolePermissionService
      .searchMyAppliedPermissions({
        permissionIds: ['create_child_usergroup'],
      })
      .pipe(
        map((perms) => perms.length > 0),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  saveData() {
    this.actionUserGroupForm
      .saveFormData()
      .pipe(
        tap(() => {
          this.isEditForm
            ? this.toastrService.success(
                'Team was updated successfully.',
                'Success'
              )
            : this.toastrService.success(
                'Team was added successfully',
                'Success'
              );
        }),
        catchError((err, caught) => {
          console.error(err);
          const errorBlob = JSON.stringify(err?.error ?? err ?? '');
          const isGuestPromotionError = /Guest user/i.test(errorBlob);
          const isUsdaDispatcherError =
            /Dispatcher Role to USDA/i.test(errorBlob);
          const isNonUsdaAdminError =
            /Non-USDA\.gov user/i.test(errorBlob);
          const isOneRolePerTeamError = /only hold one role/i.test(errorBlob);
          let friendlyMessage: string;
          if (isGuestPromotionError) {
            friendlyMessage =
              'Guest users cannot be assigned as a team member, supervisor, or admin.';
          } else if (isUsdaDispatcherError) {
            friendlyMessage = 'Dispatchers can only be non-USDA.gov users.';
          } else if (isNonUsdaAdminError) {
            friendlyMessage =
              'Admin, supervisor, and team member roles can only be assigned to USDA.gov users.';
          } else if (isOneRolePerTeamError) {
            friendlyMessage =
              'User already has a role. Remove it before assigning a new one.';
          } else {
            friendlyMessage = this.isEditForm
              ? `Team was not updated successfully. Error: ${err}`
              : `Team was not saved successfully. Error: ${err}`;
          }
          this.toastrService.error(friendlyMessage, 'Error');
          return of(null);
        }),

        tap(() => this.viewAndEditModal.close('Team sucessfully edited'))
      )
      .subscribe();
  }

  deleteData() {
    this.actionUserGroupForm
      .deleteData()
      .pipe(
        tap(() => {
          return this.toastrService.success(
            'Team was deleted successfully.',
            'Success'
          );
        }),
        catchError((err) => {
          console.error(err);
          return of(
            this.toastrService.error(
              `Team was not deleted successfully. Error: ${err}`,
              'Error'
            )
          );
        }),
        tap(() => this.deleteUserGroupModal.close('Team succesfully deleted')),
        tap(() => this.viewAndEditModal.close('Team succesfully deleted'))
      )
      .subscribe();
  }
}
